/**
 * Tours results: the page appears at once from the stored result and no row is checked with its supplier until the agent presses its price
 * (Levan, 23.10.2026: no check of the whole page or of the next page). The press opens Details; the Details CalcPackage is the check of that
 * one row and its outcome is shown on the row. QA doc: docs features/tour-search-performance/qa.md "Phase 2" - the row states and R-P2-4,
 * R-P2-6, R-P2-17 still hold for a pressed row.
 */
import { TourPackage } from '@appTypes/api.types';

/** `check.state` of a row (qa.md "Row states"): `checking` while the Details answer of the row is outstanding. */
export type TourRowCheckState = 'checking' | 'confirmed' | 'changed' | 'unavailable' | 'failed';

/** The check of one row, made when the agent pressed its price. */
export interface TourRowCheck {
  state: TourRowCheckState;
  /** `changed`: the total the row showed before the check (the new one is the row's own total) */
  previousTotalPrice?: number | null;
  errorCode?: string | null;
  supplier?: string | null;
  traceId?: string | null;
  /** `changed`: the Details package with the checked total, shown in the row's place */
  package?: TourPackage | null;
}

/** A row's check plus what only the screen knows. */
export interface TourRowCheckView extends TourRowCheck {
  /** R-P2-6: the agent pressed "Show anyway" on an `unavailable` row - its Details are offered again */
  shownAnyway?: boolean;
}

/** What the screen keeps about the rows checked on the page being shown: `response.checks`. */
export interface TourChecksModel {
  /** row key -> its check (R-P2-4: rows are matched by key, never by position); only the rows the agent pressed */
  rows: Record<string, TourRowCheckView>;
  /** R-P2-17: a changed price broke the order of a price sort - "Prices changed - sort again" */
  resort: boolean;
}

/** Dismiss ("Remove from list") names the row by its key. */
export interface ShownRowActionRequest {
  pagingId: string;
  pageNumber: number;
  key: string;
  sortByField?: string | null;
  sortOrder?: string | null;
  /** the page size, so the answer's page count follows it */
  pageRowCount?: number;
}

export interface ShownDismissResponse {
  pagingId?: string;
  totalCount: number;
  pageCount: number;
}

/** The key a row is matched by (`cacheKey`, else `id` - online-api `ShownPageConfirmation.KeyOf`). */
export const tourRowKey = (item: { cacheKey?: string | null; id?: string | null } | null | undefined): string =>
  item?.cacheKey?.length ? item.cacheKey : (item?.id ?? '');

/** The key as a test-id suffix: lower case kebab, non-alphanumerics folded (C16). */
export const tourRowTestKey = (key: string): string =>
  key
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .join('-');

/** R-P2-6 / C3: an unavailable row offers no Details until the agent presses "Show anyway". */
export const isTourDetailsBlocked = (check: TourRowCheckView | null | undefined): boolean =>
  check?.state === 'unavailable' && !check.shownAnyway;

/** The difference of totals the Details price dialog ignores (`CommonHelpersService` PRICE_THRESHOLD): the row says "changed" only beyond it. */
export const TOUR_PRICE_CHANGE_THRESHOLD = 1;
